import { Box } from '@mui/material'
import { Modal } from 'Components/Modal'
import { useEffect, useState } from 'react'
import { InterestFormEditableText } from 'Pages/ISDSettings/InterestFormLanguageCustomization/InterestFormEditableText'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { type InterestFormLabel } from 'core/types'

interface InterestFormField {
  header: string
  labelKey: string
  defaultValue: string
  previousValue?: string
  newValue?: string
}

interface InterestFormStep {
  name: string
  group: number
  fields: InterestFormField[]
}

export interface InterestFormLanguageModalProps {
  isdid: number
  editGroup: number
  open: boolean
  onClose: () => void
}

export function InterestFormLanguageModal (props: InterestFormLanguageModalProps): JSX.Element {
  const [interestFormSteps, setInterestFormSteps] = useState<InterestFormStep[]>([])
  const setupFormSteps = async (): Promise<void> => {
    const { response: existingLabels }: { response: InterestFormLabel[] } = await sendGet(`/Label/GetAllLabels?isdId=${props.isdid}`, {})

    const steps: InterestFormStep[] = [{
      name: 'Entry Point',
      group: 1,
      fields: [{
        header: 'Subtext under "Services for my child" button',
        labelKey: 'IF_Home_Child_Services',
        defaultValue: 'We will guide you through a process that will put you in touch with the services and programs that best meet the needs of your child (preschool, childcare, basic needs, home visiting, etc.).'
      },
      {
        header: 'Subtext under "Create a Referral" button',
        labelKey: 'IF_Home_Create_Referral',
        defaultValue: 'If you are a service provider who would like to create a referral on behalf of a family, click here to get started.'
      },
      {
        header: 'Header for "I am not sure" button',
        labelKey: 'IF_Home_IDK_Header',
        defaultValue: 'I am not sure'
      },
      {
        header: 'Subtext under "I am not sure" button',
        labelKey: 'IF_Home_IDK_Detail',
        defaultValue: 'Click here if you would like someone to reach out to you after we collect some basic information about you and your child.'
      }]
    },
    {
      name: 'Long Form',
      group: 2,
      fields: [{
        header: 'Child Form Introduction: First Child',
        labelKey: 'IF_Child_Intro_LF',
        defaultValue: 'First, we need to know a little about the child to help us find the best programs and services.'
      },
      {
        header: 'Child Form Introduction: Next Child',
        labelKey: 'IF_Child_Intro_Next_LF',
        defaultValue: 'Tell us a little about your next child.'
      },
      {
        header: 'Child Form Introduction Subtext',
        labelKey: 'IF_Child_Subtext_LF',
        defaultValue: 'If you have more than one child needing early childhood services, you\'ll have the option to add them later.'
      },
      {
        header: 'Date of Birth Label',
        labelKey: 'IF_Child_DOB_LF',
        defaultValue: 'Date of Birth'
      },
      {
        header: 'Gender Label',
        labelKey: 'IF_Child_Gender_LF',
        defaultValue: 'Gender'
      },
      {
        header: 'Has an Individualized Education Plan (IEP) Label',
        labelKey: 'IF_Child_IEP_LF',
        defaultValue: 'Has an Individualized Education Plan (IEP)'
      },
      {
        header: 'Has an Individualized Family Service Plan (IFSP) Label',
        labelKey: 'IF_Child_IFSP_LF',
        defaultValue: 'Has an Individualized Family Service Plan (IFSP)'
      },
      {
        header: 'Has an Early On Intervening Service Plan (EOIS) Label',
        labelKey: 'IF_Child_EOIS_LF',
        defaultValue: 'Has an Early On Intervening Service Plan (EOIS)'
      },
      {
        header: 'Foster Child Label',
        labelKey: 'IF_Child_Foster_LF',
        defaultValue: 'Foster Child'
      },
      {
        header: 'Currently Experiencing Homelessness Label',
        labelKey: 'IF_Child_Homeless_LF',
        defaultValue: 'Currently Experiencing Homelessness'
      },
      {
        header: 'SNAP Benefits (food assistance) Label',
        labelKey: 'IF_Child_SNAP_LF',
        defaultValue: 'SNAP Benefits (food assistance)'
      },
      {
        header: 'Street Address Label',
        labelKey: 'IF_Child_Address_LF',
        defaultValue: 'Child\'s Home Address'
      },
      {
        header: 'Caregiver Collection Intro',
        labelKey: 'IF_Caregiver_Intro_LF',
        defaultValue: 'Next, we need to know a little about the household members to see if they qualify for any additional assistance.'
      },
      {
        header: 'Caregiver Collection Additional Members',
        labelKey: 'IF_Caregiver_Additional_Members_LF',
        defaultValue: 'Would you like to add additional caregivers to your network?'
      },
      {
        header: 'Care Giver Collection Subtext',
        labelKey: 'IF_Caregiver_Intro_Next_LF',
        defaultValue: 'Great! Please let us know the following information.'
      },
      {
        header: 'Income Documentation Notice',
        labelKey: 'IF_Caregiver_Documentation_Notice_LF',
        defaultValue: 'Income documentation may be required later in the process. The best documentation of income is the latest tax return. If this is not available, 4 weeks of pay stubs or a W-2 form may be used until a tax return is received.'
      },
      {
        header: 'Is Caregiver / Guardian Label',
        labelKey: 'IF_Caregiver_Guardian_LF',
        defaultValue: 'Is Caregiver / Guardian'
      },
      {
        header: 'Preferred Contact Method Label',
        labelKey: 'IF_Caregiver_Contact_Method_LF',
        defaultValue: 'Preferred Contact Method'
      },
      {
        header: 'Lives at a Different Address than the Child Label',
        labelKey: 'IF_Caregiver_Different_Address_LF',
        defaultValue: 'Lives at a Different Address than the Child'
      },
      {
        header: 'Caregiver\'s Home Address Label',
        labelKey: 'IF_Caregiver_Address_LF',
        defaultValue: 'Caregiver\'s Home Address'
      },
      {
        header: 'Total Number of Household Members label',
        labelKey: 'IF_Caregiver_Household_Members_LF',
        defaultValue: 'Total Number of Household Members'
      },
      {
        header: 'Service Intro',
        labelKey: 'IF_Service_Intro_LF',
        defaultValue: 'Thank you! Here are some services and resources that may be available to you and your children.'
      },
      {
        header: 'Service Intro Subtext',
        labelKey: 'IF_Service_Subtext_LF',
        defaultValue: 'If your children need multiple or different services, you will have the ability to add that later. Select a service below to get started.'
      },
      {
        header: 'Service Date Intro',
        labelKey: 'IF_Service_Date_Intro_LF',
        defaultValue: 'When are you looking to start this service?'
      },
      {
        header: 'Service Date Subtext',
        labelKey: 'IF_Service_Date_Subtext_LF',
        defaultValue: 'If your children need multiple or different services, you will have the ability to add that later.'
      },
      {
        header: 'Service Time Intro',
        labelKey: 'IF_Service_Time_Intro_LF',
        defaultValue: 'What time of day do you desire this service?'
      },
      {
        header: 'Service Selection Intro',
        labelKey: 'IF_Service_Select_Intro_LF',
        defaultValue: 'Thank you! Which of the following best describes how you would like to proceed?'
      },
      {
        header: 'Service Browse Intro',
        labelKey: 'IF_Service_Browse_Intro_LF',
        defaultValue: 'Awesome! Here is a list of providers in your area. Select any that you may be interested in learning more about.'
      },
      {
        header: 'Additional Service Browse Intro',
        labelKey: 'IF_Service_Browse_Additional_LF',
        defaultValue: 'Are there additional services that you need at this time?'
      },
      {
        header: 'Service Search Intro',
        labelKey: 'IF_Service_Search_Intro_LF',
        defaultValue: 'Great news! Where would you like to go?'
      },
      {
        header: 'How did you hear about us',
        labelKey: 'IF_How_Did_You_Hear_LF',
        defaultValue: 'How did you hear about us?'
      }
      ]
    },
    {
      name: 'Short Form',
      group: 3,
      fields: [{
        header: 'Child Form Introduction: First Child',
        labelKey: 'IF_Child_Intro',
        defaultValue: 'First, we need to know a little about the child to help us find the best programs and services.'
      },
      {
        header: 'Child Form Introduction: Next Child',
        labelKey: 'IF_Child_Intro_Next',
        defaultValue: 'Tell us a little about your next child.'
      },
      {
        header: 'Child Form Introduction Subtext',
        labelKey: 'IF_Child_Subtext',
        defaultValue: 'If you have more than one child needing early childhood services, you\'ll have the option to add them later.'
      },
      {
        header: 'Date of Birth Label',
        labelKey: 'IF_Child_DOB',
        defaultValue: 'Date of Birth'
      },
      {
        header: 'Gender Label',
        labelKey: 'IF_Child_Gender',
        defaultValue: 'Gender'
      },
      {
        header: 'Has an Individualized Education Plan (IEP) Label',
        labelKey: 'IF_Child_IEP',
        defaultValue: 'Has an Individualized Education Plan (IEP)'
      },
      {
        header: 'Has an Individualized Family Service Plan (IFSP) Label',
        labelKey: 'IF_Child_IFSP',
        defaultValue: 'Has an Individualized Family Service Plan (IFSP)'
      },
      {
        header: 'Has an Early On Intervening Service Plan (EOIS) Label',
        labelKey: 'IF_Child_EOIS',
        defaultValue: 'Has an Early On Intervening Service Plan (EOIS)'
      },
      {
        header: 'Foster Child Label',
        labelKey: 'IF_Child_Foster',
        defaultValue: 'Foster Child'
      },
      {
        header: 'Currently Experiencing Homelessness Label',
        labelKey: 'IF_Child_Homeless',
        defaultValue: 'Currently Experiencing Homelessness'
      },
      {
        header: 'SNAP Benefits (food assistance) Label',
        labelKey: 'IF_Child_SNAP',
        defaultValue: 'SNAP Benefits (food assistance)'
      },
      {
        header: 'Street Address Label',
        labelKey: 'IF_Child_Address',
        defaultValue: 'Child\'s Home Address'
      },
      {
        header: 'Caregiver Collection Intro',
        labelKey: 'IF_Caregiver_Intro',
        defaultValue: 'Next, we need to know a little about the household members to see if they qualify for any additional assistance.'
      },
      {
        header: 'Caregiver Collection Additional Members',
        labelKey: 'IF_Caregiver_Additional_Members',
        defaultValue: 'Would you like to add additional caregivers to your network?'
      },
      {
        header: 'Care Giver Collection Subtext',
        labelKey: 'IF_Caregiver_Intro_Next',
        defaultValue: 'Great! Please let us know the following information.'
      },
      {
        header: 'Income Documentation Notice',
        labelKey: 'IF_Caregiver_Documentation_Notice',
        defaultValue: 'Income documentation may be required later in the process. The best documentation of income is the latest tax return. If this is not available, 4 weeks of pay stubs or a W-2 form may be used until a tax return is received.'
      },
      {
        header: 'Is Caregiver / Guardian Label',
        labelKey: 'IF_Caregiver_Guardian',
        defaultValue: 'Is Caregiver / Guardian'
      },
      {
        header: 'Preferred Contact Method Label',
        labelKey: 'IF_Caregiver_Contact_Method',
        defaultValue: 'Preferred Contact Method'
      },
      {
        header: 'Lives at a Different Address than the Child Label',
        labelKey: 'IF_Caregiver_Different_Address',
        defaultValue: 'Lives at a Different Address than the Child'
      },
      {
        header: 'Caregiver\'s Home Address Label',
        labelKey: 'IF_Caregiver_Address',
        defaultValue: 'Caregiver\'s Home Address'
      },
      {
        header: 'Total Number of Household Members label',
        labelKey: 'IF_Caregiver_Household_Members',
        defaultValue: 'Total Number of Household Members'
      }]
    },
    {
      name: 'Referral',
      group: 4,
      fields: [{
        header: 'Child Form Introduction: First Child',
        labelKey: 'IF_Child_Intro_Ref',
        defaultValue: 'First, we need to know a little about the child to help us find the best programs and services.'
      },
      {
        header: 'Child Form Introduction: Next Child',
        labelKey: 'IF_Child_Intro_Next_Ref',
        defaultValue: 'Tell us a little about your next child.'
      },
      {
        header: 'Child Form Introduction Subtext',
        labelKey: 'IF_Child_Subtext_Ref',
        defaultValue: 'If you have more than one child needing early childhood services, you\'ll have the option to add them later.'
      },
      {
        header: 'Date of Birth Label',
        labelKey: 'IF_Child_DOB_Ref',
        defaultValue: 'Date of Birth'
      },
      {
        header: 'Gender Label',
        labelKey: 'IF_Child_Gender_Ref',
        defaultValue: 'Gender'
      },
      {
        header: 'Has an Individualized Education Plan (IEP) Label',
        labelKey: 'IF_Child_IEP_Ref',
        defaultValue: 'Has an Individualized Education Plan (IEP)'
      },
      {
        header: 'Has an Individualized Family Service Plan (IFSP) Label',
        labelKey: 'IF_Child_IFSP_Ref',
        defaultValue: 'Has an Individualized Family Service Plan (IFSP)'
      },
      {
        header: 'Has an Early On Intervening Service Plan (EOIS) Label',
        labelKey: 'IF_Child_EOIS_Ref',
        defaultValue: 'Has an Early On Intervening Service Plan (EOIS)'
      },
      {
        header: 'Foster Child Label',
        labelKey: 'IF_Child_Foster_Ref',
        defaultValue: 'Foster Child'
      },
      {
        header: 'Currently Experiencing Homelessness Label',
        labelKey: 'IF_Child_Homeless_Ref',
        defaultValue: 'Currently Experiencing Homelessness'
      },
      {
        header: 'SNAP Benefits (food assistance) Label',
        labelKey: 'IF_Child_SNAP_Ref',
        defaultValue: 'SNAP Benefits (food assistance)'
      },
      {
        header: 'Street Address Label',
        labelKey: 'IF_Child_Address_Ref',
        defaultValue: 'Child\'s Home Address'
      },
      {
        header: 'Caregiver Collection Intro',
        labelKey: 'IF_Caregiver_Intro_Ref',
        defaultValue: 'Next, we need to know a little about the household members to see if they qualify for any additional assistance.'
      },
      {
        header: 'Caregiver Collection Additional Members',
        labelKey: 'IF_Caregiver_Additional_Members_Ref',
        defaultValue: 'Would you like to add additional caregivers to your network?'
      },
      {
        header: 'Care Giver Collection Subtext',
        labelKey: 'IF_Caregiver_Intro_Next_Ref',
        defaultValue: 'Great! Please let us know the following information.'
      },
      {
        header: 'Income Documentation Notice',
        labelKey: 'IF_Caregiver_Documentation_Notice_Ref',
        defaultValue: 'Income documentation may be required later in the process. The best documentation of income is the latest tax return. If this is not available, 4 weeks of pay stubs or a W-2 form may be used until a tax return is received.'
      },
      {
        header: 'Is Caregiver / Guardian Label',
        labelKey: 'IF_Caregiver_Guardian_Ref',
        defaultValue: 'Is Caregiver / Guardian'
      },
      {
        header: 'Preferred Contact Method Label',
        labelKey: 'IF_Caregiver_Contact_Method_Ref',
        defaultValue: 'Preferred Contact Method'
      },
      {
        header: 'Lives at a Different Address than the Child Label',
        labelKey: 'IF_Caregiver_Different_Address_Ref',
        defaultValue: 'Lives at a Different Address than the Child'
      },
      {
        header: 'Caregiver\'s Home Address Label',
        labelKey: 'IF_Caregiver_Address_Ref',
        defaultValue: 'Caregiver\'s Home Address'
      },
      {
        header: 'Total Number of Household Members label',
        labelKey: 'IF_Caregiver_Household_Members_Ref',
        defaultValue: 'Total Number of Household Members'
      }]
    },
    {
      name: 'Login',
      group: 5,
      fields: [{
        header: 'Account Mismatch Warning',
        labelKey: 'IF_Account_Warning',
        defaultValue: 'The email address associated with your account does not match any of the entered caregivers. You can either update your email address or edit the caregivers to match. If you update your email address, you will be required to log in again.'
      }]
    },
    {
      name: 'Logged in Features',
      group: 6,
      fields: [{
        header: 'Child Form Introduction: First Child',
        labelKey: 'IF_Child_Intro_LI',
        defaultValue: 'First, we need to know a little about the child to help us find the best programs and services.'
      },
      {
        header: 'Child Form Introduction: Next Child',
        labelKey: 'IF_Child_Intro_Next_LI',
        defaultValue: 'Tell us a little about your next child.'
      },
      {
        header: 'Child Form Introduction Subtext',
        labelKey: 'IF_Child_Subtext_LI',
        defaultValue: 'If you have more than one child needing early childhood services, you\'ll have the option to add them later.'
      },
      {
        header: 'Date of Birth Label',
        labelKey: 'IF_Child_DOB_LI',
        defaultValue: 'Date of Birth'
      },
      {
        header: 'Gender Label',
        labelKey: 'IF_Child_Gender_LI',
        defaultValue: 'Gender'
      },
      {
        header: 'Has an Individualized Education Plan (IEP) Label',
        labelKey: 'IF_Child_IEP_LI',
        defaultValue: 'Has an Individualized Education Plan (IEP)'
      },
      {
        header: 'Has an Individualized Family Service Plan (IFSP) Label',
        labelKey: 'IF_Child_IFSP_LI',
        defaultValue: 'Has an Individualized Family Service Plan (IFSP)'
      },
      {
        header: 'Has an Early On Intervening Service Plan (EOIS) Label',
        labelKey: 'IF_Child_EOIS_LI',
        defaultValue: 'Has an Early On Intervening Service Plan (EOIS)'
      },
      {
        header: 'Foster Child Label',
        labelKey: 'IF_Child_Foster_LI',
        defaultValue: 'Foster Child'
      },
      {
        header: 'Currently Experiencing Homelessness Label',
        labelKey: 'IF_Child_Homeless_LI',
        defaultValue: 'Currently Experiencing Homelessness'
      },
      {
        header: 'SNAP Benefits (food assistance) Label',
        labelKey: 'IF_Child_SNAP_LI',
        defaultValue: 'SNAP Benefits (food assistance)'
      },
      {
        header: 'Street Address Label',
        labelKey: 'IF_Child_Address_LI',
        defaultValue: 'Child\'s Home Address'
      },
      {
        header: 'Caregiver Collection Intro',
        labelKey: 'IF_Caregiver_Intro_LI',
        defaultValue: 'Next, we need to know a little about the household members to see if they qualify for any additional assistance.'
      },
      {
        header: 'Caregiver Collection Additional Members',
        labelKey: 'IF_Caregiver_Additional_Members_LI',
        defaultValue: 'Would you like to add additional caregivers to your network?'
      },
      {
        header: 'Care Giver Collection Subtext',
        labelKey: 'IF_Caregiver_Intro_Next_LI',
        defaultValue: 'Great! Please let us know the following information.'
      },
      {
        header: 'Income Documentation Notice',
        labelKey: 'IF_Caregiver_Documentation_Notice_LI',
        defaultValue: 'Income documentation may be required later in the process. The best documentation of income is the latest tax return. If this is not available, 4 weeks of pay stubs or a W-2 form may be used until a tax return is received.'
      },
      {
        header: 'Is Caregiver / Guardian Label',
        labelKey: 'IF_Caregiver_Guardian_LI',
        defaultValue: 'Is Caregiver / Guardian'
      },
      {
        header: 'Preferred Contact Method Label',
        labelKey: 'IF_Caregiver_Contact_Method_LI',
        defaultValue: 'Preferred Contact Method'
      },
      {
        header: 'Lives at a Different Address than the Child Label',
        labelKey: 'IF_Caregiver_Different_Address_LI',
        defaultValue: 'Lives at a Different Address than the Child'
      },
      {
        header: 'Caregiver\'s Home Address Label',
        labelKey: 'IF_Caregiver_Address_LI',
        defaultValue: 'Caregiver\'s Home Address'
      },
      {
        header: 'Total Number of Household Members label',
        labelKey: 'IF_Caregiver_Household_Members_LI',
        defaultValue: 'Total Number of Household Members'
      },
      {
        header: 'Service Intro',
        labelKey: 'IF_Service_Intro_LI',
        defaultValue: 'Thank you! Here are some services and resources that may be available to you and your children.'
      },
      {
        header: 'Service Intro Subtext',
        labelKey: 'IF_Service_Subtext_LI',
        defaultValue: 'If your children need multiple or different services, you will have the ability to add that later. Select a service below to get started.'
      },
      {
        header: 'Service Date Intro',
        labelKey: 'IF_Service_Date_Intro_LI',
        defaultValue: 'When are you looking to start this service?'
      },
      {
        header: 'Service Date Subtext',
        labelKey: 'IF_Service_Date_Subtext_LI',
        defaultValue: 'If your children need multiple or different services, you will have the ability to add that later.'
      },
      {
        header: 'Service Time Intro',
        labelKey: 'IF_Service_Time_Intro_LI',
        defaultValue: 'What time of day do you desire this service?'
      },
      {
        header: 'Service Selection Intro',
        labelKey: 'IF_Service_Select_Intro_LI',
        defaultValue: 'Thank you! Which of the following best describes how you would like to proceed?'
      },
      {
        header: 'Service Browse Intro',
        labelKey: 'IF_Service_Browse_Intro_LI',
        defaultValue: 'Awesome! Here is a list of providers in your area. Select any that you may be interested in learning more about.'
      },
      {
        header: 'Additional Service Browse Intro',
        labelKey: 'IF_Service_Browse_Additional_LI',
        defaultValue: 'Are there additional services that you need at this time?'
      },
      {
        header: 'Service Search Intro',
        labelKey: 'IF_Service_Search_Intro_LI',
        defaultValue: 'Great news! Where would you like to go?'
      }]
    },

    {
      name: 'Submission',
      group: 7,
      fields: [{
        header: 'Final Summary Rank',
        labelKey: 'IF_Final_Summary_Rank',
        defaultValue: 'Due to high demand, we would like you to rank your selected service providers by preference. This will ensure that your interest form will be seen and evaluated by your most desired providers first.'
      },
      {
        header: 'Authorization Text',
        labelKey: 'IF_Authorization_Text',
        defaultValue: 'By submitting this application, I acknowledge that the information given within will be shared with (the intake provider) agencies to support my family needs. By typing my name below, it serves as my electronic signature and permission.'
      },
      {
        header: 'Non-Discrimination Disclaimer',
        labelKey: 'IF_NonDiscriminationDisclaimer',
        defaultValue: 'We do not discriminate on the basis of race, color, religion, sex, national origin, age, height, weight, marital status, handicap, disability, or limited English proficiency in any of our programs or activities.'
      }
      ]
    },
    {
      name: 'Post Submit',
      group: 8,
      fields: [{
        header: 'Post Submit Greeting',
        labelKey: 'IF_Post_Submit_Greeting',
        defaultValue: 'While you wait to hear back, you can complete the rest of your MiECC profile by adding additional dependents, other guardians who can act on their behalf, or any missing information and documentation not included in this interest form. Having an up-to-date profile can help us identify missing services or resources that you may find helpful in the future. Thank you again!'
      }]
    }]

    // set newValue and previousValue for all values.
    steps.forEach((step) => {
      step.fields.forEach((field) => {
        field.previousValue = existingLabels.find((label) => label.name === field.labelKey)?.labelText ?? ''
        field.newValue = field.previousValue
      })
    })

    setInterestFormSteps(steps.filter(s => s.group === props.editGroup))
  }

  useEffect(() => {
    void setupFormSteps()
  }, [])

  const handleClose = (): void => {
    props.onClose()
  }

  const handleSave = async (): Promise<void> => {
    await sendPost(`/Label/UpdateLabels?isdId=${props.isdid}`, interestFormSteps[0].fields.map((field) => {
      return {
        name: field.labelKey,
        labelText: field.newValue
      }
    }))

    props.onClose()
  }

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>, fieldIndex: number): void => {
    const newSteps = [...interestFormSteps]
    const newStep = { ...newSteps[0] }
    const newField = { ...newStep.fields[fieldIndex] }
    if (newField.previousValue !== event.target.value) {
      newField.newValue = event.target.value
      newStep.fields[fieldIndex] = newField
      newSteps[0] = newStep
      setInterestFormSteps(newSteps)
    }
  }

  if (interestFormSteps.length === 0) return <></>

  const content = <>
    <Box>
      {interestFormSteps[0].fields.map((field, index) => {
        return <InterestFormEditableText
          key={index}
          header={field.header}
          labelKey={field.labelKey}
          defaultValue={field.defaultValue}
          newValue={field.newValue ?? ''}
          onChange={(e) => { handleTextChange(e, index) }}
        />
      })}

    </Box>
  </>

  return <Modal
    open={props.open}
    title={`${interestFormSteps[0].name} Language Customization`}
    confirmationContent={content}
    maxWidth='md'
    cancelButtonText='Discard Changes'
    confirmButtonText='Update'
    buttonClassName='modal-button'
    onClose={handleClose}
    onConfirm={handleSave}
  />
}
